'use client';

import { Card } from '@app/app/_components/Card/Card';
import { ChevronUpIcon } from '@app/app/_icons/ChevronUpIcon';
import { ReactNode, useCallback, useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { motion } from 'motion/react';

interface Props {
    label: ReactNode;
    defaultCollapsed?: boolean;
    children: ReactNode;
    labelClassName?: string;
    childrenClassName?: string;
    className?: string;
    labelContainerClassName?: string;
    actionElements?: ReactNode;
}

export function CollapsableCard({
    label,
    defaultCollapsed = false,
    children,
    labelClassName,
    className,
    labelContainerClassName,
    childrenClassName,
    actionElements,
}: Props) {
    const [open, setOpen] = useState(!defaultCollapsed);

    const handleToggle = useCallback((e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();

        setOpen(isOpen => !isOpen);
    }, []);

    return (
        <Card className={twMerge('p-6', className)}>
            <div className="flex flex-col">
                <div className={twMerge('flex w-full items-center justify-between', labelContainerClassName)}>
                    <div className="flex flex-1 items-center">
                        <button
                            className="flex flex-1 cursor-pointer items-center justify-between"
                            onClick={handleToggle}>
                            <div className={twMerge('flex text-start text-2xl font-medium', labelClassName)}>
                                {label}
                            </div>
                            <div className="ml-2">
                                <ChevronUpIcon
                                    className={twJoin('size-5 transition-transform', !open && 'rotate-180')}
                                />
                            </div>
                        </button>
                    </div>
                </div>
                {actionElements && <div className="flex items-center">{actionElements}</div>}
            </div>

            <motion.div
                className={twMerge('overflow-hidden', open && 'overflow-visible')}
                initial={{
                    height: open ? 'auto' : 0,
                }}
                animate={{
                    height: open ? 'auto' : 0,
                }}>
                <div className={twMerge('pt-6', childrenClassName)}>{children}</div>
            </motion.div>
        </Card>
    );
}
